import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import firebaseService from '../services/firebaseService';
import logger from '../utils/logger';
import { generateReviewId } from '../utils/reviewIdGenerator';
import analytics from '../utils/analytics';
import { getVerificationMode } from '../config/verificationConfig';
import { generateGeohash } from '../utils/geohashUtils';
import toast from 'react-hot-toast';

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const { currentUser: user } = useAuth();
  const [restrooms, setRestrooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRestrooms, setUserRestrooms] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use a Set to store geohash prefixes that have already been fetched
  const fetchedGeohashPrefixes = useRef(new Set());

  // Clear fetched prefixes when user changes (login/logout)
  useEffect(() => {
    logger.log('[DataContext] User changed, clearing fetched geohash prefixes cache.');
    fetchedGeohashPrefixes.current.clear();
  }, [user]);

  const fetchRestrooms = useCallback(async (options = {}) => {
    // Skip Firebase fetching on marketing page for non-authenticated users only
    // DemoMap has its own data and authenticated users should redirect to /app
    if (typeof window !== 'undefined' && window.location.pathname === '/' && !user) {
      setRestrooms([]);
      setLoading(false);
      return;
    }

    // If geohash prefixes are provided, implement caching logic
    if (options.geohashPrefixes && options.geohashPrefixes.length > 0) {
      const prefixesToFetch = options.geohashPrefixes.filter(prefix => 
        !fetchedGeohashPrefixes.current.has(prefix)
      );

      if (prefixesToFetch.length === 0) {
        logger.log('✅ All requested geohash prefixes already fetched. Serving from accumulated state.');
        // Still update state to trigger re-render in MapScreen if needed, even if no new data
        setRestrooms(prev => [...prev]);
        setLoading(false);
        return; // No new data to fetch for these prefixes
      }

      logger.log(`🔄 Fetching restrooms for new geohash prefixes: ${prefixesToFetch.join(', ')}`);
      options.geohashPrefixes = prefixesToFetch; // Only request the new prefixes
    } else {
      logger.log('🔄 Fetching all restrooms (no geohash prefixes provided).');
    }

    try {
      setLoading(true);
      const restroomData = await firebaseService.getRestrooms(user?.isAdmin, user?.id, options);
      
      // Add newly fetched prefixes to the set
      if (options.geohashPrefixes && options.geohashPrefixes.length > 0) {
        options.geohashPrefixes.forEach(prefix => fetchedGeohashPrefixes.current.add(prefix));
      }

      // Merge with existing restrooms and deduplicate
      setRestrooms(prev => {
        const combined = [...prev, ...restroomData];
        const seen = new Set();
        return combined.filter(r => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
      });
      
      logger.log(`✅ Loaded ${restroomData.length} restrooms from backend.`);
    } catch (error) {
      logger.error('❌ Error fetching restrooms:', error);
      // Only clear if not an incremental fetch
      if (!options.geohashPrefixes && !options.limit) {
        setRestrooms([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchUsers = useCallback(async () => {
    try {
      logger.log('🔄 Fetching users from backend...');
      const userData = await firebaseService.getUsers();
      setUsers(userData);
      logger.log(`✅ Loaded ${userData.length} users from backend`);
    } catch (error) {
      logger.error('❌ Error fetching users:', error);
      setUsers([]);
    }
  }, []);

  const fetchUserRestrooms = useCallback(async () => {
    if (!user) return;
    try {
      const data = await firebaseService.getUserRestrooms(user.id);
      setUserRestrooms(data);
    } catch (error) {
      logger.error('❌ Error fetching user restrooms:', error);
    }
  }, [user]);

  const fetchUserReviews = useCallback(async () => {
    if (!user) return;
    try {
      const data = await firebaseService.getUserReviews(user.id);
      setUserReviews(data);
    } catch (error) {
      logger.error('❌ Error fetching user reviews:', error);
    }
  }, [user]);

  // Initialize verification mode cache and load restrooms on mount and when user changes
  useEffect(() => {
    const initialize = async () => {
      // Only fetch restrooms if user is available OR if we're in demo mode
      if (user || window.location.pathname === '/') {
        // Pre-load verification mode into cache if user is available
        if (user) {
          await getVerificationMode();
        }
        // Then fetch restrooms and users (users only if authenticated)
        if (user) {
          // MapScreen will handle fetching restrooms based on viewport.
          // We only fetch a small amount or nothing here to speed up initial load.
          await Promise.all([
            fetchUsers(),
            fetchUserRestrooms(),
            fetchUserReviews()
          ]);
        } else {
          // Demo mode: fetch restrooms without users
          await fetchRestrooms({ limit: 100 });
        }
      } else {
        // Clear restrooms and users when user logs out
        setRestrooms([]);
        setUsers([]);
        setUserRestrooms([]);
        setUserReviews([]);
        setLoading(false);
      }
    };
    initialize();
  }, [user, fetchUsers, fetchUserRestrooms, fetchUserReviews]); // Removed fetchRestrooms dependency to allow manual control in MapScreen if needed, 
                         // but wait, we still want initial load for non-MapScreen pages.

  const getRestroomById = useCallback(async (id, forceFetch = false) => {
    // Map results may be lightweight restroom_map_index documents. Fetch the
    // full document when the caller explicitly requests it.
    if (!forceFetch) {
      const local = restrooms.find(r => r.id === id);
      if (local) return local;
    }

    // Fetch from backend
    try {
      const restroom = await firebaseService.getRestroomById(id);
      if (restroom) {
        // Optionally add to state if not there
        setRestrooms(prev => {
          const existingIndex = prev.findIndex(r => r.id === id);
          if (existingIndex === -1) return [...prev, restroom];
          const updated = [...prev];
          updated[existingIndex] = restroom;
          return updated;
        });
      }
      return restroom;
    } catch (error) {
      logger.error('❌ Error getting restroom by ID:', error);
      return null;
    }
  }, [restrooms]);


  const addRestroom = async (restroomData) => {
    try {
      // Create initial review from the creation data
      const initialReview = {
        id: generateReviewId(restroomData),
        userId: user.id,
        username: user.username,
        rating: parseFloat(restroomData.rating) || 3,
        comment: restroomData.description || '',
        reviewerRestroomType: restroomData.reviewerRestroomType || 'unisex',
        isClean: restroomData.isClean,
        hasHandSoap: restroomData.hasHandSoap,
        hasPaperTowels: restroomData.hasPaperTowels,
        hasChangingTable: restroomData.hasChangingTable,
        hasHandicapAccess: restroomData.hasHandicapAccess,
        hasFeminineProducts: restroomData.hasFeminineProducts || false,
        hasBidet: restroomData.hasBidet,
        requiresKey: restroomData.requiresKey,
        hasShowers: restroomData.hasShowers,
        restroomType: restroomData.restroomType,
        stallGapSize: restroomData.stallGapSize,
        createdAt: new Date().toISOString(),
        likes: 0,
        likedBy: []
      };

      const newRestroom = {
        ...restroomData,
        id: `restroom-${Date.now()}`,
        createdAt: new Date().toISOString(),
        createdBy: user.id,
        createdByUsername: user.username,
        verified: user.isAdmin, // Auto-verify admin restrooms
        reviews: [initialReview], // Include the initial review
        reports: [],
        ratingCount: 1, // One review from creator
        averageRating: parseFloat(restroomData.rating) || 3, // Use form rating as number, fallback to 3
        geohash: generateGeohash(parseFloat(restroomData.latitude), parseFloat(restroomData.longitude))
      };
      
      console.log('📊 DataContext - newRestroom object:', newRestroom);
      console.log('🎆 DataContext - rating:', restroomData.rating, 'averageRating:', newRestroom.averageRating);
      console.log('📍 DataContext - newRestroom coordinates:', newRestroom.latitude, newRestroom.longitude); // Added log

      console.log('💾 Attempting to save restroom:', newRestroom.name);
      await firebaseService.saveRestroom(newRestroom);
      console.log('✅ Restroom saved successfully');
      
      // Update user restrooms and reviews
      fetchUserRestrooms();
      fetchUserReviews();
      
      // Track restroom submission analytics
      analytics.trackRestroomSubmission({
        category: newRestroom.category,
        rating: newRestroom.rating,
        hasTruckParking: newRestroom.hasTruckParking || false,
        isAdmin: user.isAdmin || false
      });
      
      return newRestroom;
    } catch (error) {
      console.error('Error adding restroom:', error);
      throw error;
    }
  };

  const addReview = async (restroomId, reviewData, reviewerLocation = null) => {
    try {
      const success = await firebaseService.addReview(
        restroomId, 
        {
          ...reviewData,
          userId: user.id,
          username: user.username
        },
        reviewerLocation
      );
      
      if (success) {
        // Update user reviews
        fetchUserReviews();

        // Track review submission analytics
        const restroom = restrooms.find(r => r.id === restroomId);
        analytics.trackReviewSubmission({
          rating: reviewData.rating,
          restroom_id: restroomId,
          restroom_name: restroom?.name || 'Unknown'
        });
        
        await fetchRestrooms(); // Refresh from backend
        return true;
      }
      throw new Error('Failed to add review');
    } catch (error) {
      console.error('Error adding review:', error);
      throw error;
    }
  };

  const reportRestroom = async (restroomId, reportData) => {
    try {
      const success = await firebaseService.reportRestroom(restroomId, {
        ...reportData,
        userId: user.id,
        username: user.username
      });
      
      if (success) {
        await fetchRestrooms(); // Refresh from backend
        return true;
      }
      throw new Error('Failed to report restroom');
    } catch (error) {
      console.error('Error reporting restroom:', error);
      throw error;
    }
  };

  const deleteRestroom = async (restroomId) => {
    try {
      const success = await firebaseService.deleteRestroom(restroomId);
      if (success) {
        await fetchRestrooms(); // Refresh from backend
        return true;
      }
      throw new Error('Failed to delete restroom');
    } catch (error) {
      console.error('Error deleting restroom:', error);
      throw error;
    }
  };

  const deleteUser = async (userId) => {
    try {
      const success = await firebaseService.deleteUser(userId);
      if (success) {
        // Note: AdminDashboard handles its own user list refresh
        // since it has a local users state
        return true;
      }
      throw new Error('Failed to delete user');
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  const verifyRestroom = async (restroomId) => {
    try {
      const success = await firebaseService.verifyRestroom(restroomId, user.id);
      if (success) {
        await fetchRestrooms(); // Refresh from backend
        return true;
      }
      throw new Error('Failed to verify restroom');
    } catch (error) {
      console.error('Error verifying restroom:', error);
      throw error;
    }
  };

  const mergeRestrooms = async (sourceId, targetId) => {
    try {
      const success = await firebaseService.mergeRestrooms(sourceId, targetId);
      if (success) {
        await fetchRestrooms(); // Refresh from backend
        return true;
      }
      throw new Error('Failed to merge restrooms');
    } catch (error) {
      console.error('Error merging restrooms:', error);
      throw error;
    }
  };

  const getVerifiedRestrooms = () => {
    return restrooms.filter(r => r.verified);
  };

  const getNearbyRestrooms = async (latitude, longitude, radiusMiles = 5) => {
    try {
      // Calculate nearby restrooms from current state
      const nearby = restrooms.filter(restroom => {
        const distance = calculateDistance(latitude, longitude, restroom.latitude, restroom.longitude);
        return distance <= radiusMiles;
      });
      return nearby;
    } catch (error) {
      console.error('Error getting nearby restrooms:', error);
      return [];
    }
    };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Haversine formula for calculating distance between two coordinates
    const R = 3959; // Radius of Earth in MILES (changed from 6371 km)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Returns distance in miles
  };

  // Like or unlike a review
  const likeReview = async (reviewId) => {
    try {
      const result = await firebaseService.likeReview(reviewId, user.id);
      
      if (result.isLiked) {
        toast.success('Thanks for the wipe of approval! 🧻');
      } else {
        toast.success('Wipe of approval removed 🧻');
      }
      
      // Update local state immediately for better UX
      setRestrooms(prevRestrooms => {
        return prevRestrooms.map(restroom => {
          const reviews = restroom.reviews || [];
          const reviewIndex = reviews.findIndex(r => r.id === reviewId);
          
          if (reviewIndex !== -1) {
            const updatedReviews = [...reviews];
            const review = updatedReviews[reviewIndex];
            
            // Initialize likes arrays if needed
            if (!review.likedBy) review.likedBy = [];
            if (review.likes === undefined) review.likes = 0;
            
            // Update the review
            if (result.isLiked) {
              review.likes++;
              review.likedBy.push(user.id);
            } else {
              review.likes--;
              review.likedBy = review.likedBy.filter(id => id !== user.id);
            }
            
            updatedReviews[reviewIndex] = review;
            return { ...restroom, reviews: updatedReviews };
          }
          
          return restroom;
        });
      });
      
      return result;
    } catch (error) {
      console.error('Error liking review:', error);
      toast.error('Failed to like review');
      throw error;
    }
  };

  const searchRestrooms = useCallback(async (searchQuery) => {
    try {
      return await firebaseService.searchRestrooms(searchQuery);
    } catch (error) {
      logger.error('❌ Error searching restrooms:', error);
      return [];
    }
  }, []);

  const updateRestroom = useCallback(async (restroomId, updates) => {
    try {
      await firebaseService.updateRestroom(restroomId, updates);
      
      // Update local state
      setRestrooms(prev => prev.map(r => 
        r.id === restroomId ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
      ));
      
      return true;
    } catch (error) {
      logger.error('❌ Error updating restroom:', error);
      throw error;
    }
  }, []);

  const value = {
    restrooms,
    setRestrooms, // Expose setRestrooms
    users,
    loading,
    addRestroom,
    updateRestroom,
    addReview,
    reportRestroom,
    deleteRestroom,
    deleteUser,
    verifyRestroom,
    mergeRestrooms,
    getVerifiedRestrooms,
    getNearbyRestrooms,
    calculateDistance,
    likeReview,
    fetchRestrooms,
    searchRestrooms,
    getRestroomById,
    userRestrooms,
    userReviews,
    fetchUserRestrooms,
    fetchUserReviews
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}